<template>
  <div class="tasks-page">
    <!-- Header Card -->
    <b-card class="mb-2">
      <b-row class="align-items-center">
        <b-col
          cols="12"
          md="6"
        >
          <h3 class="mb-0">
            <feather-icon
              icon="CheckSquareIcon"
              size="24"
              class="mr-50"
            />
            إدارة المهام
          </h3>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="text-md-right mt-1 mt-md-0"
        >
          <!-- View Toggle -->
          <b-button-group class="mr-1">
            <b-button
              :variant="currentView === 'kanban' ? 'primary' : 'outline-primary'"
              @click="currentView = 'kanban'"
            >
              <feather-icon
                icon="ColumnsIcon"
                size="16"
                class="mr-50"
              />
              <span class="d-none d-sm-inline">كانبان</span>
            </b-button>
            <b-button
              :variant="currentView === 'list' ? 'primary' : 'outline-primary'"
              @click="currentView = 'list'"
            >
              <feather-icon
                icon="ListIcon"
                size="16"
                class="mr-50"
              />
              <span class="d-none d-sm-inline">قائمة</span>
            </b-button>
          </b-button-group>

          <!-- Refresh Button -->
          <b-button
            variant="outline-primary"
            :disabled="loading"
            @click="refreshTasks"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'rotate-animation': loading }"
            />
          </b-button>
        </b-col>
      </b-row>

      <!-- Stats Row -->
      <b-row class="mt-2">
        <b-col
          cols="6"
          md="3"
          class="mb-1 mb-md-0"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-warning"
              size="45"
              rounded
            >
              <feather-icon
                icon="ClockIcon"
                size="20"
              />
            </b-avatar>
            <div class="ml-1">
              <h4 class="mb-0">
                {{ getTaskCountByStatus('pending') }}
              </h4>
              <small class="text-muted">قيد الانتظار</small>
            </div>
          </div>
        </b-col>
        <b-col
          cols="6"
          md="3"
          class="mb-1 mb-md-0"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-info"
              size="45"
              rounded
            >
              <feather-icon
                icon="PlayIcon"
                size="20"
              />
            </b-avatar>
            <div class="ml-1">
              <h4 class="mb-0">
                {{ getTaskCountByStatus('in_progress') }}
              </h4>
              <small class="text-muted">قيد التنفيذ</small>
            </div>
          </div>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-primary"
              size="45"
              rounded
            >
              <feather-icon
                icon="EyeIcon"
                size="20"
              />
            </b-avatar>
            <div class="ml-1">
              <h4 class="mb-0">
                {{ getTaskCountByStatus('review') }}
              </h4>
              <small class="text-muted">قيد المراجعة</small>
            </div>
          </div>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <div class="d-flex align-items-center">
            <b-avatar
              variant="light-success"
              size="45"
              rounded
            >
              <feather-icon
                icon="CheckIcon"
                size="20"
              />
            </b-avatar>
            <div class="ml-1">
              <h4 class="mb-0">
                {{ getTaskCountByStatus('completed') }}
              </h4>
              <small class="text-muted">مكتملة</small>
            </div>
          </div>
        </b-col>
      </b-row>
    </b-card>

    <!-- Content -->
    <div
      v-if="loading && !tasks.length"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        label="جاري التحميل..."
      />
    </div>

    <div v-else>
      <!-- Kanban View -->
      <tasks-kanban-view
        v-if="currentView === 'kanban'"
        :tasks="tasks"
        @refresh="refreshTasks"
        @edit-task="editTask"
      />

      <!-- List View -->
      <tasks-list-view
        v-if="currentView === 'list'"
        :tasks="tasks"
        :loading="loading"
        @refresh="refreshTasks"
        @filter-change="handleFilterChange"
      />
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BButtonGroup,
  BSpinner,
  BAvatar,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import TasksKanbanView from './TasksKanbanView.vue'
import TasksListView from './TasksListView.vue'

export default {
  name: 'TasksPage',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BButtonGroup,
    BSpinner,
    BAvatar,
    TasksKanbanView,
    TasksListView,
  },
  data() {
    return {
      currentView: 'kanban', // 'kanban' or 'list'
      filter: {
        search: null,
        status: null,
        priority: null,
      },
    }
  },
  computed: {
    ...mapGetters({
      tasksData: 'tasks/tasks',
      loading: 'tasks/load',
    }),
    tasks() {
      // Handle different data structures safely
      if (!this.tasksData) {
        return []
      }
      if (this.tasksData.data && this.tasksData.data.tasks) {
        return this.tasksData.data.tasks
      }
      if (Array.isArray(this.tasksData)) {
        return this.tasksData
      }
      if (this.tasksData.data && Array.isArray(this.tasksData.data)) {
        return this.tasksData.data
      }
      return []
    },
  },
  watch: {
    currentView(newView) {
      // Save view preference
      localStorage.setItem('tasksView', newView)
    },
  },
  mounted() {
    this.loadTasks()

    // Load saved view preference
    const savedView = localStorage.getItem('tasksView')
    if (savedView) {
      this.currentView = savedView
    }
  },
  methods: {
    loadTasks() {
      this.$store.dispatch('tasks/myTasks', {
        query: {
          ...this.filter,
          page: 1,
        },
      })
    },
    refreshTasks() {
      this.loadTasks()
    },
    getTaskCountByStatus(status) {
      if (!this.tasks || !Array.isArray(this.tasks)) {
        return 0
      }
      return this.tasks.filter(task => task.status === status).length
    },
    handleFilterChange(filters) {
      this.filter = { ...this.filter, ...filters }
      // You can add API filtering here if needed
    },
    editTask(task) {
      // Navigate to edit page or open modal
      this.$router.push({ name: 'taskDetails', params: { id: task.id } })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/app-tasks.scss';
</style>

<style lang="scss" scoped>
.tasks-page {
  .rotate-animation {
    animation: rotate 1s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
}
</style>
